import React, { useState, useMemo } from 'react';
import './App.css';
import RedirectTree from './components/RedirectTree';
import FileUpload from './components/FileUpload';
import TabNavigation from './components/TabNavigation';
import NestedRedirects from './components/NestedRedirects';
import DuplicateRedirects from './components/DuplicateRedirects';
import { parseRedirects } from './utils/redirectParser';
import { 
  analyzeNestedRedirects, 
  findDuplicateRedirects,
  getNestedRedirectStats,
  getDuplicateRedirectStats 
} from './utils/redirectAnalyzer';
import { 
  analyzeDuplicatesSEO,
  analyzeNestedSEO 
} from './utils/seoAnalyzer';

function App() {
  const [redirectsData, setRedirectsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('tree');

  const handleFileUpload = async (file) => {
    setLoading(true);
    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);
      setRedirectsData(jsonData);
    } catch (error) {
      console.error('Error parsing file:', error);
      alert('Error parsing the JSON file. Please make sure it\'s a valid JSON file.');
    } finally {
      setLoading(false);
    }
  };

  const treeData = useMemo(() => {
    if (!redirectsData) return null;
    return parseRedirects(redirectsData);
  }, [redirectsData]);

  const nestedRedirects = useMemo(() => {
    if (!redirectsData) return [];
    const nested = analyzeNestedRedirects(redirectsData);
    return analyzeNestedSEO(nested, redirectsData);
  }, [redirectsData]);

  const duplicateRedirects = useMemo(() => {
    if (!redirectsData) return [];
    const duplicates = findDuplicateRedirects(redirectsData);
    return analyzeDuplicatesSEO(duplicates, redirectsData);
  }, [redirectsData]);

  const nestedStats = useMemo(() => {
    return getNestedRedirectStats(nestedRedirects);
  }, [nestedRedirects]);

  const duplicateStats = useMemo(() => {
    return getDuplicateRedirectStats(duplicateRedirects);
  }, [duplicateRedirects]);

  const tabs = [
    {
      id: 'tree',
      label: 'Hierarchy Tree',
      icon: '🌳',
      count: redirectsData?.length
    },
    {
      id: 'nested',
      label: 'Nested Redirects',
      icon: '🔗',
      count: nestedStats.totalChains
    },
    {
      id: 'duplicates',
      label: 'Duplicate Targets',
      icon: '📋',
      count: duplicateStats.totalDuplicateTargets
    }
  ];

  return (
    <div className="App">
      <header className="App-header">
        <h1>Website Redirects Analyzer</h1>
        <p>Upload your redirects JSON file to analyze the redirect structure</p>
      </header>
      
      <main className="App-main">
        {!redirectsData ? (
          <FileUpload onFileUpload={handleFileUpload} loading={loading} />
        ) : (
          <div className="results-container">
            <div className="results-header">
              <h2>Redirect Analysis Results</h2>
              <p>Total redirects analyzed: {redirectsData.length}</p>
              <button 
                className="reset-button"
                onClick={() => setRedirectsData(null)}
              >
                Upload New File
              </button>
            </div>
            
            <TabNavigation 
              activeTab={activeTab}
              onTabChange={setActiveTab}
              tabs={tabs}
            />
            
            <div className="tab-content">
              {activeTab === 'tree' && <RedirectTree treeData={treeData} />}
              {activeTab === 'nested' && (
                <NestedRedirects 
                  nestedRedirects={nestedRedirects}
                  stats={nestedStats}
                />
              )}
              {activeTab === 'duplicates' && (
                <DuplicateRedirects 
                  duplicates={duplicateRedirects}
                  stats={duplicateStats}
                />
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App; 